import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCurrency } from '@medical-counsellor/shared';
import { estimatesApi } from '@/api/client';
import {
    TrendingUp,
    TrendingDown,
    AlertCircle,
    CheckCircle,
    Save,
    Plus
} from 'lucide-react';

interface VarianceAnalysisProps {
    estimate: any;
}

interface VarianceReason {
    category: string;
    amount: number;
    notes?: string;
}

export function VarianceAnalysis({ estimate }: VarianceAnalysisProps) {
    const queryClient = useQueryClient();
    const actual = estimate.actualProcedure;
    const [reasons, setReasons] = useState<VarianceReason[]>([]);
    const [isAutoDetected, setIsAutoDetected] = useState(false);

    // Fetch existing variance analysis
    const { data: existingVariance, isLoading } = useQuery({
        queryKey: ['variance', estimate.id],
        queryFn: () => estimatesApi.getVariance(estimate.id),
        enabled: !!estimate.id,
    });

    // Fetch variance categories
    const { data: categories } = useQuery({
        queryKey: ['variance-categories'],
        queryFn: () => estimatesApi.getVarianceCategories(),
    });

    // Calculate base values
    const estimatedTotal = estimate.totalAmount;
    const actualTotal = actual?.actualTotalAmount || 0;
    const varianceAmount = actualTotal - estimatedTotal;
    const variancePercent = estimatedTotal ? (varianceAmount / estimatedTotal) * 100 : 0;

    // Initialize state
    useEffect(() => {
        if (existingVariance && existingVariance.varianceReasons) {
            setReasons(existingVariance.varianceReasons);
            setIsAutoDetected(false);
        } else if (actual && !isAutoDetected) {
            // Auto-detect variances
            const autoReasons: VarianceReason[] = [];

            // 1. Stay Variance
            if (actual.actualStayDays !== estimate.stayDays) {
                // Estimate room cost per day
                // This is rough estimation if we don't have per-line access easily, 
                // but we can try to find "Room Charges" section
                const estRoomLine = estimate.lines.find((l: any) => l.sectionName === 'Room Charges' || l.sectionName === 'Room Rent');
                const estRoomRate = estRoomLine ? estRoomLine.unitPrice : 0;

                const stayDiff = actual.actualStayDays - estimate.stayDays;
                if (estRoomRate > 0 && stayDiff !== 0) {
                    autoReasons.push({
                        category: stayDiff > 0 ? 'EXTENDED_STAY' : 'REDUCED_STAY',
                        amount: stayDiff * estRoomRate,
                        notes: `${Math.abs(stayDiff)} day(s) ${stayDiff > 0 ? 'extra' : 'less'} x ${formatCurrency(estRoomRate)}`
                    });
                }
            }

            // 2. Room Type Variance
            if (actual.actualRoomType && actual.actualRoomType !== estimate.roomType) {
                // Hard to calc exact amount without knowing rate diff, but we can flag it
                // Placeholder amount 0, user can adjust
                autoReasons.push({
                    category: 'ROOM_UPGRADE', // Generic, user can change
                    amount: 0,
                    notes: `Changed from ${estimate.roomType} to ${actual.actualRoomType}`
                });
            }

            // 3. Unexplained / Other
            const explainedAmount = autoReasons.reduce((sum, r) => sum + r.amount, 0);
            const remaining = varianceAmount - explainedAmount;

            if (Math.abs(remaining) > 1) {
                autoReasons.push({
                    category: 'OTHER',
                    amount: remaining,
                    notes: 'Uncategorized variance'
                });
            }

            setReasons(autoReasons);
            setIsAutoDetected(true);
        }
    }, [estimate, actual, existingVariance, isAutoDetected, varianceAmount]);

    const saveMutation = useMutation({
        mutationFn: (data: any) => estimatesApi.saveVariance(estimate.id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['variance', estimate.id] });
            setTimeout(() => {
                saveMutation.reset();
            }, 3000);
        },
    });

    const handleSave = () => {
        saveMutation.mutate({
            estimatedTotal,
            actualTotal,
            varianceAmount,
            variancePercent,
            varianceReasons: reasons,
        });
    };

    const updateReason = (index: number, field: keyof VarianceReason, value: any) => {
        const newReasons = [...reasons];
        newReasons[index] = { ...newReasons[index], [field]: value };
        setReasons(newReasons);
    };

    const addReason = () => {
        setReasons([...reasons, { category: 'OTHER', amount: 0, notes: '' }]);
    };

    const removeReason = (index: number) => {
        setReasons(reasons.filter((_, i) => i !== index));
    };

    const getVarianceColor = (amount: number) => {
        if (Math.abs(amount) < 1) return 'text-slate-500';
        return amount > 0 ? 'text-red-600' : 'text-green-600';
    };

    if (!actual) return null;

    // --- Helper for Line Item Comparison ---
    const groupLines = (lines: any[]) => {
        return lines.reduce((acc: Record<string, any[]>, line) => {
            const section = line.sectionName || 'Other';
            if (!acc[section]) acc[section] = [];
            acc[section].push(line);
            return acc;
        }, {});
    };

    const estSections = groupLines(estimate.lines || []);
    const actSections = groupLines(actual.lines || []);
    const allSections = Array.from(new Set([...Object.keys(estSections), ...Object.keys(actSections)]));

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header / Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card bg-slate-50 border-slate-200">
                    <div className="card-body">
                        <p className="text-sm font-medium text-slate-500">Estimated Total</p>
                        <p className="text-2xl font-bold text-slate-900">{formatCurrency(estimatedTotal)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {estimate.stayDays} days • {estimate.roomType}
                        </p>
                    </div>
                </div>

                <div className="card bg-white border-slate-200">
                    <div className="card-body">
                        <p className="text-sm font-medium text-slate-500">Actual Billed</p>
                        <p className="text-2xl font-bold text-slate-900">{formatCurrency(actualTotal)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {actual.actualStayDays ?? '-'} days • {actual.actualRoomType ?? '-'}
                        </p>
                    </div>
                </div>

                <div className={`card border-l-4 ${varianceAmount > 0 ? 'border-l-red-500 bg-red-50' : varianceAmount < 0 ? 'border-l-green-500 bg-green-50' : 'border-l-slate-400 bg-slate-50'}`}>
                    <div className="card-body">
                        <p className="text-sm font-medium text-slate-500">Net Variance</p>
                        <div className="flex items-center gap-2">
                            <p className={`text-2xl font-bold ${getVarianceColor(varianceAmount)}`}>
                                {varianceAmount > 0 ? '+' : ''}{formatCurrency(varianceAmount)}
                            </p>
                            <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${varianceAmount > 0 ? 'bg-red-100 text-red-700' : varianceAmount < 0 ? 'bg-green-100 text-green-700' : 'bg-slate-200'}`}>
                                {varianceAmount > 0 ? '+' : ''}{variancePercent.toFixed(1)}%
                            </span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                            {varianceAmount > 0 ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-green-500" />}
                            <p className="text-xs text-slate-500">
                                {varianceAmount > 0 ? 'Over Estimate' : varianceAmount < 0 ? 'Under Estimate' : 'Exact Match'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Variance Reasons Breakdown */}
            <div className="card border-slate-200 shadow-sm">
                <div className="card-header bg-slate-50 border-b border-slate-100 py-3 flex justify-between items-center">
                    <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-slate-500" />
                        Variance Breakdown
                    </h3>
                    <button
                        onClick={handleSave}
                        className={`btn btn-sm ${saveMutation.isSuccess ? 'btn-success text-white' : 'btn-primary'}`}
                        disabled={saveMutation.isPending}
                    >
                        {saveMutation.isPending ? (
                            <>Saving...</>
                        ) : saveMutation.isSuccess ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Saved</>
                        ) : (
                            <><Save className="w-3 h-3 mr-1" /> Save Analysis</>
                        )}
                    </button>
                </div>
                <div className="p-4 bg-white">
                    <div className="space-y-3">
                        {reasons.map((reason, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                                <div className="w-full sm:w-1/4">
                                    <select
                                        className="select select-bordered select-sm w-full"
                                        value={reason.category}
                                        onChange={(e) => updateReason(idx, 'category', e.target.value)}
                                    >
                                        <option value="">Select Category</option>
                                        {categories?.map((c: any) => (
                                            <option key={c.code} value={c.code}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="w-full sm:w-1/4">
                                    <input
                                        type="number"
                                        className={`input input-sm w-full font-medium ${reason.amount > 0 ? 'text-red-600' : 'text-green-600'}`}
                                        value={reason.amount}
                                        onChange={(e) => updateReason(idx, 'amount', Number(e.target.value))}
                                        placeholder="Amount"
                                    />
                                </div>
                                <div className="w-full sm:w-1/2 flex gap-2">
                                    <input
                                        type="text"
                                        className="input input-sm w-full"
                                        value={reason.notes || ''}
                                        onChange={(e) => updateReason(idx, 'notes', e.target.value)}
                                        placeholder="Explanation notes..."
                                    />
                                    <button
                                        onClick={() => removeReason(idx)}
                                        className="btn btn-ghost btn-sm text-slate-400 hover:text-red-500"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={addReason}
                            className="btn btn-ghost btn-sm text-primary-600 hover:bg-primary-50 self-start"
                        >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Reason
                        </button>
                    </div>

                    {/* Check if fully explained */}
                    <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center text-sm">
                        <span className="text-slate-500">Explained Amount:</span>
                        <div className="flex items-center gap-4">
                            <span className="font-semibold">{formatCurrency(reasons.reduce((s, r) => s + r.amount, 0))}</span>
                            {Math.abs(varianceAmount - reasons.reduce((s, r) => s + r.amount, 0)) > 1 && (
                                <span className="text-amber-600 text-xs flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {formatCurrency(varianceAmount - reasons.reduce((s, r) => s + r.amount, 0))} remaining
                                </span>
                            )}
                            {Math.abs(varianceAmount - reasons.reduce((s, r) => s + r.amount, 0)) <= 1 && (
                                <span className="text-green-600 text-xs flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Fully explained
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Detailed Line Item Comparison */}
            <div className="card shadow-sm border-slate-200">
                <div className="card-header border-b bg-slate-50 px-6 py-4 flex justify-between items-center">
                    <h3 className="font-semibold text-slate-800">Line Item Comparison</h3>
                    <div className="text-xs text-slate-500 flex gap-4">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300"></span> Estimate</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-800"></span> Actual</span>
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 border-b">
                                <tr>
                                    <th className="px-6 py-3 text-left font-medium w-1/3">Item / Description</th>
                                    <th className="px-6 py-3 text-right font-medium">Estimated</th>
                                    <th className="px-6 py-3 text-right font-medium">Actual</th>
                                    <th className="px-6 py-3 text-right font-medium">Diff</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {allSections.map(section => {
                                    const estItems = estSections[section] || [];
                                    const actItems = actSections[section] || [];
                                    const itemMap = new Set([...estItems.map(i => i.itemName), ...actItems.map(i => i.itemName)]);
                                    const items = Array.from(itemMap).sort();

                                    const sectEstTotal = estItems.reduce((sum, i) => sum + i.lineTotal, 0);
                                    const sectActTotal = actItems.reduce((sum, i) => sum + i.lineTotal, 0);
                                    const sectDiff = sectActTotal - sectEstTotal;

                                    return (
                                        <>
                                            <tr className="bg-slate-50/80 font-semibold text-slate-700">
                                                <td className="px-6 py-2 text-xs uppercase tracking-wider">{section}</td>
                                                <td className="px-6 py-2 text-right">{formatCurrency(sectEstTotal)}</td>
                                                <td className="px-6 py-2 text-right">{formatCurrency(sectActTotal)}</td>
                                                <td className={`px-6 py-2 text-right ${getVarianceColor(sectDiff)}`}>
                                                    {sectDiff > 0 ? '+' : ''}{formatCurrency(sectDiff)}
                                                </td>
                                            </tr>
                                            {items.map(itemName => {
                                                const estItem = estItems.find(i => i.itemName === itemName);
                                                const actItem = actItems.find(i => i.itemName === itemName);

                                                const estVal = estItem?.lineTotal || 0;
                                                const actVal = actItem?.lineTotal || 0;
                                                const diff = actVal - estVal;

                                                if (estVal === 0 && actVal === 0) return null;

                                                return (
                                                    <tr key={`${section}-${itemName}`} className="hover:bg-slate-50 group">
                                                        <td className="px-6 py-3 pl-8 text-slate-600">
                                                            <div className="font-medium group-hover:text-slate-900 transition-colors">{itemName}</div>
                                                            <div className="text-xs text-slate-400 mt-0.5 flex gap-4">
                                                                {estItem && <span>Est: {estItem.quantity} &times; {formatCurrency(estItem.unitPrice)}</span>}
                                                                {actItem && <span>Act: {actItem.quantity} &times; {formatCurrency(actItem.unitPrice)}</span>}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3 text-right text-slate-500">
                                                            {estVal ? formatCurrency(estVal) : '-'}
                                                        </td>
                                                        <td className="px-6 py-3 text-right font-medium text-slate-900">
                                                            {actVal ? formatCurrency(actVal) : '-'}
                                                        </td>
                                                        <td className={`px-6 py-3 text-right font-medium ${getVarianceColor(diff)}`}>
                                                            {diff === 0 ? '-' : `${diff > 0 ? '+' : ''}${formatCurrency(diff)}`}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
